"use client";
import { useEffect } from 'react';
import { BlogPanel } from './BlogPanel';
import BlogFilters from './BlogFilters';
import { useState } from 'react';
import { FilterOption } from './BlogFilters';

type Props = {
  id: string, 
  displayName: string,
  index: number,
  filters: any,
  activeTab: number,
  typeToGather: string[],
  allCards?: object[],
  config?: any,
  loadMoreText?: string,
  mainCardFilters?: FilterOption[],
  lang?: string,
  cardDisplayMode?: string
}

export default function ResourceGridTab(
  { 
    id, 
    displayName, 
    index,
    filters,
    activeTab,
    typeToGather,
    allCards,
    config,
    mainCardFilters,
    lang,
    cardDisplayMode
  }: Props,
) {

  const labelSortByLabel = config?.commonTextLabels?.labelSortBy || "Sort By";
  const newestLabel = config?.commonTextLabels?.labelForNewest || "Newest";
  const oldestLabel = config?.commonTextLabels?.labelForOldest || "Oldest";
  const loadMoreLabel = config?.commonTextLabels?.labelForLoadMoreResults || "Load More";
  const labelNoResults = config?.commonTextLabels?.labelForNoResults || "No Results";
  const labelForClearAll = config?.commonTextLabels?.labelForClearAll || "Clear All";

  const [selectedSorting, setSelectedSorting] = useState<FilterOption[]>([{ title: newestLabel, id: newestLabel}]);
  const [showLoadMore, setShowLoadMore] = useState(true);
  const [selectedFilters, setSelectedFilters] = useState<FilterOption[]>([]);
  const [tabCards, setTabCards] = useState<any[]>([]);
  const [formattedCards, setFormattedCards] = useState<any[]| undefined>([]);
  const sortingOptions = [newestLabel, oldestLabel];
  const initialCardLimit = 18;
  const [cardLimit, setCardLimit] = useState(initialCardLimit);

  const handleFilterChange = (selected: FilterOption[]) => {
    setSelectedFilters(selected);
  };

  const handleSortingChange = (selected: FilterOption[]) => {
    setSelectedSorting(selected);
  };

  const handleChipClick = (index: number, setActiveTab: Function) => {
    if (index === activeTab) return; // Prevent re-clicking the same chip
    setActiveTab(index);
  };

  const handleChipDelete = (item: FilterOption) => {
    setSelectedFilters(selectedFilters?.filter((selectedFilter: FilterOption) => selectedFilter?.id !== item?.id));
  };

  const formattedCardTags = (card: any ) => {
    return card?.tagsCollection?.items?.map((tag: any) => { 
      return { 
        id: tag?.sys?.id, 
        title: tag?.displayName, 
        category: { 
          id: tag?.categoryCollection?.items[0]?.sys?.id, 
          title: tag?.categoryCollection?.items[0]?.displayName 
        }
      } 
    }) 
  }

  const checkGridCardType = (typeToGather: string[], card: any) => {
    const checkTypes = typeToGather?.map((type: string) => {
      if(type === "Articles") {
        return "Article";
      } else if(type === "Case Studies") {
        return "CaseStudy";
      } else if(type === "Documents") {
        return "Document";
      } else if (type === "Videos") {
        return "Video";
      } else if (type === "External Resources") {
        return "ExternalResource";
      } else if (type === "Customer Stories") {
        return "CustomerStory";
      }
      return null;
    })
    const cardType = card?.typeName;
    if(checkTypes?.includes(cardType)) {
      return true;
    }
    return false;
  }
  
  useEffect(() => {
    // include only the cards which match the tab's type
    const typeFilteredCards = allCards?.filter((card: any) => {
      return checkGridCardType(typeToGather, card);
    })
    const formattedFilteredCards = typeFilteredCards?.map((card: any) => {
      card.cardTags = formattedCardTags(card);
      return card
    })?.filter((card: any) => {
      const shallowTabFilters = mainCardFilters?.map((filter: any) => { return filter?.id });
      const shallowCardTags = card?.cardTags?.map((tag: any) => { return tag?.id });

      // ensure cards with no tags don't get returned
      // if(shallowCardTags?.length > 0) { 
      //   return shallowTabFilters?.every((item: any) => shallowCardTags?.includes(item));
      // } else {
      //   return false;
      // };
      // BW: Some Cards not having tags, isn't a problem. I think!
      if(cardDisplayMode === "OR"){
        // return cards which have at least one tag from the tab filters list
        return shallowTabFilters?.some((item: any) => shallowCardTags?.includes(item));
      }
      // return cards which have every tag in the tab filters list
      return shallowTabFilters?.every((item: any) => shallowCardTags?.includes(item));

    })
    setFormattedCards(formattedFilteredCards)
  }, [allCards, allCards?.length, mainCardFilters, typeToGather, cardDisplayMode])
  

  useEffect(() => {
    const returnFilteredCards = (cards: any[], filters: FilterOption[]) => {
      // group filters by tag category (industry, or product etc)
      const categorizedFilters = Object?.groupBy(filters, ({ category }) => { return category?.id || "undefined" })
      return cards?.filter((card: any) => {
        let displayCard = false;
        // rearrange card tags into shallow array for comparison
        const shallowCardTags = card?.cardTags?.map((tag: any) => { return tag?.id });
        Object?.values(categorizedFilters)?.every((categoryFilters: any) => {
          // filters inside a grouped category
          displayCard = categoryFilters?.some((categoryFilter: any) => {
            // if the card has a tag which matches the category filter, return true
            return shallowCardTags?.includes(categoryFilter?.id);
          })
          if(!displayCard) {
            // break out of the loop and don't display card if the tags don't match at least one from each category
            return false;
          } else {
            return true
          }
        });
        return displayCard;
      })
    }
    if(formattedCards){
      let cards = formattedCards;
      if(selectedFilters?.length > 0){
        cards = returnFilteredCards(cards, selectedFilters);
      }
      // Prioritize cards whose type matches displayName if displayName is in typeToGather
      let displayNameLower = displayName.toLowerCase()
      // If displayNameLower is 'casestudies', use 'casestudy' for filter logic
      let filterTypeName = displayNameLower.replace(/\s+/g, '') === 'casestudies' ? 'casestudy' : displayNameLower;
      const typeToGatherLower = typeToGather?.map(t => t.toLowerCase()) || [];

      let prioritizedCards = cards;
      if (typeToGatherLower.includes(displayNameLower)) {
        prioritizedCards = [
          ...cards.filter(card => (card?.typeName?.toLowerCase?.().replace(/\s+/g, '') === filterTypeName)),
          ...cards.filter(card => (card?.typeName?.toLowerCase?.().replace(/\s+/g, '') !== filterTypeName))
        ];
      }

      const sortedCards = prioritizedCards?.sort((a, b) => {
        if(selectedSorting[0]?.title === oldestLabel){
          if (a?.date < b?.date) {
            return -1;
          } else if (a?.date > b?.date) {
            return 1;
          }
          // a must be equal to b
          return 0;
        } else {
          if (a?.date > b?.date) {
            return -1;
          } else if (a?.date < b?.date) {
            return 1;
          }
          // a must be equal to b
          return 0;
        }
      })
      const limitedCards = sortedCards?.slice(0, cardLimit);
      if(sortedCards?.length <= cardLimit){
        setShowLoadMore(false);
      } else {
        setShowLoadMore(true);
      }
      setTabCards(limitedCards);
    }
  }, [selectedFilters, selectedSorting, cardLimit, formattedCards, formattedCards?.length, showLoadMore, oldestLabel, displayName, typeToGather]);

  const loadMoreHandler = () => {
    setCardLimit(cardLimit + initialCardLimit);
  }

  return (
    <>
      <BlogFilters
        gridFilters={filters || []}
        selectedFilters={selectedFilters}
        sortingOptions={sortingOptions}
        labelSortBy={labelSortByLabel}
        labelClearAll={labelForClearAll}
        handleFilterChange={handleFilterChange}
        handleSortingChange={handleSortingChange}
        selectedSorting={selectedSorting}
        setSelectedSorting={setSelectedSorting}
        handleChipDelete={handleChipDelete}
      />
      <BlogPanel
        cards={tabCards}
        activeTab={activeTab}
        loadMoreText={ loadMoreLabel }
        loadMoreHandler={loadMoreHandler}
        showLoadMore={showLoadMore}
        imageType="asImage"
        noResultsText={ labelNoResults }
        lang={lang}
      />
    </>
  );
}
